import React, { PureComponent } from 'react';
import { MapContext } from 'react-openlayers';
import { Coordinate } from 'ol/coordinate';
import { convertProjection3857 } from '../../../utils';
import { alertSuccess } from '../../../alertDispatcher';

interface CoordinateDisplayLayerProps {
  enabled: boolean;
  onCoordinateChange: (lat: number | null, lon: number | null) => void;
  onClick: (lat: number, lon: number) => void;
}

/**
 * Layer component that handles coordinate tracking on the map.
 * - Tracks mouse movement on desktop
 * - Tracks clicks on mobile devices
 * - Converts coordinates from EPSG:3857 to EPSG:4326 (lat/lon)
 * - Copies coordinates to clipboard on click
 */
export default class CoordinateDisplayLayer extends PureComponent<CoordinateDisplayLayerProps> {
  static contextType = MapContext;
  declare context: React.ContextType<typeof MapContext>;

  componentDidMount() {
    if (this.props.enabled) {
      this.attachEventListeners();
    }
  }

  componentDidUpdate(prevProps: CoordinateDisplayLayerProps) {
    if (this.props.enabled !== prevProps.enabled) {
      if (this.props.enabled) {
        this.attachEventListeners();
      } else {
        this.removeEventListeners();
      }
    }
  }

  componentWillUnmount() {
    this.removeEventListeners();
  }

  attachEventListeners = () => {
    const map = this.context?.map;

    if (!map) {
      return;
    }

    if (this.isMobileDevice()) {
      map.on('click', this.handleClick);
    } else {
      map.on('pointermove', this.handlePointerMove);
      map.on('click', this.handleClick);
    }
  };

  removeEventListeners = () => {
    const map = this.context?.map;

    if (!map) {
      return;
    }

    map.un('pointermove', this.handlePointerMove);
    map.un('click', this.handleClick);
  };

  isMobileDevice = (): boolean => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  handlePointerMove = (event: any) => {
    const coordinate = event.coordinate;
    this.updateCoordinates(coordinate);
  };

  handleClick = async (event: any) => {
    const coordinate = event.coordinate;
    this.updateCoordinates(coordinate);

    // Copy to clipboard
    const [lat, lon] = convertProjection3857(coordinate);
    const formattedText = `${lat.toFixed(6)},${lon.toFixed(6)}`;

    try {
      await navigator.clipboard.writeText(formattedText);
      alertSuccess(`Coordinates copied: ${formattedText}`);
    } catch (error) {
      console.error('Failed to copy coordinates to clipboard:', error);
    }

    this.props.onClick(lat, lon);
  };

  updateCoordinates = (coordinate: Coordinate) => {
    const [lat, lon] = convertProjection3857(coordinate);
    this.props.onCoordinateChange(lat, lon);
  };

  render() {
    // This is a non-visual layer component
    return null;
  }
}
